import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { ArrowDown, Sparkles, TrendingUp } from 'lucide-react';

const DistillationSlide = () => {
  const models = [
    { name: 'R1-Distill-Qwen-7B', aime: '55.5%', math: '92.8%', code: '37.6%' },
    { name: 'R1-Distill-Qwen-14B', aime: '69.7%', math: '93.9%', code: '53.1%' },
    { name: 'R1-Distill-Qwen-32B', aime: '72.6%', math: '94.3%', code: '57.2%' },
    { name: 'R1-Distill-Llama-70B', aime: '70.0%', math: '94.5%', code: '57.5%' },
  ];

  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Knowledge distillation dramatically outperforms direct RL on smaller models"
      >
        Distillation Beats Direct RL
      </SlideTitle>
      
      <SlideContent>
        {/* Key Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="slide-card border-muted-foreground/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-muted-foreground">Direct RL from Scratch</h3>
                <p className="text-xs text-muted-foreground">DeepSeek-R1-Zero-Qwen-32B</p>
              </div>
            </div>
            <div className="text-center py-4">
              <p className="text-5xl font-mono text-muted-foreground">47.0%</p>
              <p className="text-sm text-muted-foreground mt-2">AIME 2024</p>
            </div>
          </div>
          
          <div className="slide-card border-accent/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
                <ArrowDown className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Distillation from R1</h3>
                <p className="text-xs text-accent">DeepSeek-R1-Distill-Qwen-32B</p>
              </div>
            </div>
            <div className="text-center py-4">
              <p className="text-5xl font-mono text-accent">72.6%</p>
              <p className="text-sm text-accent mt-2">AIME 2024</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center mb-6">
          <div className="px-6 py-3 rounded-full bg-primary/10 border border-primary/20">
            <span className="text-primary font-semibold">+25.6%</span>
            <span className="text-muted-foreground ml-2">improvement with distillation</span>
          </div>
        </div>
        
        {/* Benchmark Table */}
        <div className="slide-card overflow-hidden">
          <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Distilled Model Benchmarks
          </h3>
          
          <div className="overflow-x-auto">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>AIME 2024</th>
                  <th>MATH-500</th>
                  <th>LiveCodeBench</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model, index) => (
                  <tr key={index}>
                    <td className="font-semibold text-foreground">{model.name}</td>
                    <td><span className="text-accent font-mono">{model.aime}</span></td>
                    <td><span className="text-primary font-mono">{model.math}</span></td>
                    <td><span className="text-secondary font-mono">{model.code}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
          <p className="text-sm text-muted-foreground">
            <strong className="text-primary">Key Insight:</strong> Reasoning patterns discovered by large models during RL can be effectively transferred via knowledge distillation using 800K curated samples.
          </p>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default DistillationSlide;
